import React, { useCallback, useState } from 'react';
import { Card } from '@peoplegrove/pg-components';
import { Spin } from 'antd';

import useConversationSnapshotMetrics from '../../hooks/useConversationSnapshotMetrics';
import { useConversationSnapshotFiltersContext } from '../../context/ConversationSnapshotFiltersContext';

import classes from './ConversationSnapshots.module.scss';
import Heading from './Heading';
import SnapshotFilters from './SnapshotFilters';

const ConversationSnapshots = () => {
  const { snapshotFilters } = useConversationSnapshotFiltersContext();

  const {
    data = {},
    isLoading,
    isError,
  } = useConversationSnapshotMetrics(snapshotFilters);

  const {
    tickets_generated: ticketsGenerated,
    total_conversations: totalConversation,
    total_unique_users: totalUniqueUsers,
    total_csat_submitted: totalCSATSubmitted,
    avg_csat_score: avgCSATScore,
    total_upvoted_conversations: totalUpvotedConversations,
    ai_conversation_count: aiConversationCount,
  } = data;

  const ticketGeneratedPercentage = (
    (ticketsGenerated / totalConversation || 0) * 100
  ).toFixed(2);
  const upvotedConversationPercentage = (
    (totalUpvotedConversations / totalConversation || 0) * 100
  ).toFixed(2);

  const csatSubmissionRate = (
    (totalCSATSubmitted / totalConversation || 0) * 100
  ).toFixed(2);

  const aiConversationPercentage = (
    (aiConversationCount / totalConversation || 0) * 100
  ).toFixed(2);

  const renderMetricBody = ({ label, value, footer, footer2 }) => {
    if (isLoading) {
      return (
        <div className={classes.metricPlaceholder} aria-live="polite">
          <Spin size="small" />
        </div>
      );
    }
    if (isError) {
      return (
        <div className={classes.metricPlaceholder} aria-live="polite">
          —
        </div>
      );
    }
    return (
      <div className={classes.metricValue}>
        <div className={classes.headingValue}>{value}</div>
        <div className={classes.footerContainer}>
          <div className={classes.footerValue}>{footer}</div>
          {footer2 && (
            <>
              <div className={classes.footerSeparator}>•</div>
              <div className={classes.footerValue}>{footer2}</div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="ck-card" aria-labelledby="conversation-snapshot-heading">
      <Heading />
      <SnapshotFilters />
      <div className={classes.cardsRow}>
        <Card title="Total conversations" bordered>
          {renderMetricBody({
            value: totalConversation,
            footer: `${totalUniqueUsers} users`,
          })}
        </Card>

        <Card title="Tickets generated" bordered>
          {renderMetricBody({
            value: `${ticketGeneratedPercentage}%`,
            footer: `${ticketsGenerated} of ${totalConversation} conversations`,
          })}
        </Card>

        <Card title="Average CSAT Score" bordered>
          {renderMetricBody({
            label: 'Average CSAT score',
            value: avgCSATScore,
            footer: `${totalCSATSubmitted} responses`,
            footer2: `${csatSubmissionRate}% submission rate`,
          })}
        </Card>

        <Card title="Conversation Success Rate" bordered>
          {renderMetricBody({
            value: `${upvotedConversationPercentage}%`,
            footer: `${totalUpvotedConversations} conversations with thumbs up`,
          })}
        </Card>

        <Card title="AI conversation rate" bordered>
          {renderMetricBody({
            value: `${aiConversationPercentage}%`,
            footer: `${aiConversationCount} of ${totalConversation} conversations`,
          })}
        </Card>
      </div>
    </div>
  );
};

export default ConversationSnapshots;
